import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgRef, Site } from '@/api/endpoints';
import { SiteOptions, groupSitesByOrg } from '@/features/orgs';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';
import { usePermissions } from '@/lib/permissions';
import { cn } from '@/lib/cn';

/**
 * 当前工地：一张卡片 —— 工地名加粗、下面一行小字，右边「切换」（CR-081 方向稿，CR-082 定稿）。
 *
 * - **网页端侧栏（`nav`）**：点开是弹出面板，平台账号按公司分组（CR-076），当前那个标「当前」。
 * - **App 首页（`hero`）**：透明的原生 `<select>` 盖在卡片上，手机上弹的是系统的选择器，键盘和读屏照常。
 *
 * 在班期间（CR-015 / CR-069）换成静态的卡片 + 一句说明，不留一个点了没反应的控件。
 * 下面那行小字：平台账号写公司名（CR-076），其余写地址；都没有就写「当前工地」。单位账号的界面上不出现公司（CR-073）。
 */
export function SiteSwitcher({
  id,
  sites,
  current,
  locked,
  onChange,
  size = 'nav',
}: {
  id: string;
  sites: readonly Site[];
  current: Site | null;
  /** 在班锁定：只显示，不能换 */
  locked: boolean;
  onChange: (siteId: number) => void;
  /** nav = 网页端侧栏里；hero = App 首页顶上 */
  size?: 'nav' | 'hero';
}) {
  const { t } = useTranslation();
  const { isPlatform } = usePermissions();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const hero = size === 'hero';
  const sub = current ? (isPlatform ? current.org.name : (current.address ?? t('site.current'))) : t('site.empty');
  const canSwitch = !locked && sites.length > 1;
  usePopoverPlacement(open, rootRef, panelRef, { matchWidth: true });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const card = cn(
    'relative flex w-full items-center gap-3 rounded-xl border border-rule bg-surface text-left shadow-xs',
    hero ? 'min-h-row px-4 py-2.5' : 'min-h-touch px-3 py-2',
    canSwitch && 'cursor-pointer transition-colors hover:border-rule-strong focus-within:ring-3 focus-within:ring-brand-500/15',
  );
  const text = (
    <>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate font-semibold text-ink', hero ? 'text-title' : 'text-body')}>
          {current?.name ?? t('site.empty')}
        </span>
        <span className="block truncate text-caption text-ink-3">{locked ? t('site.lockedHint') : sub}</span>
      </span>
      {canSwitch && <span className="shrink-0 text-caption font-medium text-brand-600">{t('site.switch')}</span>}
    </>
  );

  // App：原生选择器盖在卡片上
  if (hero) {
    return (
      <label htmlFor={canSwitch ? id : undefined} className={card}>
        {text}
        {canSwitch && (
          <select
            id={id}
            aria-label={t('site.current')}
            value={current?.id ?? ''}
            onChange={(e) => {
              if (/^\d+$/.test(e.target.value)) onChange(Number(e.target.value));
            }}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          >
            {/* 平台账号按公司分组（CR-076） */}
            <SiteOptions sites={sites} />
          </select>
        )}
      </label>
    );
  }

  // 网页端：弹出面板。单位账号只有自己家的工地，不分组、不出现公司
  const groups: Array<{ org: OrgRef | null; sites: Site[] }> = isPlatform
    ? groupSitesByOrg(sites)
    : [{ org: null, sites: [...sites] }];
  return (
    <div ref={rootRef} className="relative">
      {canSwitch ? (
        <button
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-action="site.switch"
          onClick={() => setOpen((v) => !v)}
          className={card}
        >
          {text}
        </button>
      ) : (
        <div className={card}>{text}</div>
      )}
      {open && (
        <div
          ref={panelRef}
          role="listbox"
          aria-label={t('site.current')}
          className="absolute left-0 z-dropdown w-full overflow-y-auto rounded-xl border border-rule-strong bg-popover p-1 shadow-pop"
        >
          {groups.map((group) => (
            <div key={group.org?.id ?? 'all'}>
              {group.org && <div className="px-2 pb-1 pt-2 text-caption font-medium text-ink-4">{group.org.name}</div>}
              {group.sites.map((site) => {
                const selected = site.id === current?.id;
                return (
                  <button
                    key={site.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setOpen(false);
                      if (!selected) onChange(site.id);
                    }}
                    className={cn(
                      'flex min-h-touch w-full items-center gap-2 rounded-md px-2 text-left text-body hover:bg-ink/6',
                      selected ? 'font-semibold text-ink' : 'text-ink-2',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{site.name}</span>
                    {selected && <span className="shrink-0 text-caption font-normal text-ink-4">{t('site.currentShort')}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
